import {some} from '@danwang/option';
import {
  useUpdateCardBenefitMutation,
  FeePeriodsByAccountDocument,
  type UpdateCardBenefitMutation,
} from '@transactions/graphql';
import {inferSchedule} from 'common/benefitSchedule';
import {fromSeconds, today} from 'common/date';
import * as React from 'react';
import {addNewCategory} from 'src/apolloCache/updateCategories';
import {Button} from 'src/Button';
import {CreditDestinationField} from 'src/CardBenefitsView/CreditDestinationField';
import {PeriodsEditor} from 'src/CardBenefitsView/PeriodsEditor';
import {
  ScheduleEditor,
  defaultScheduleState,
  explodeScheduleState,
  scheduleStateFromInferred,
} from 'src/CardBenefitsView/ScheduleEditor';
import CategoryPicker from 'src/CategoryPicker';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import {useModals} from 'src/Modals';
import {SegmentedButton} from 'src/SegmentedButton';
import type {FetchResult} from '@apollo/client';
import type {Option} from '@danwang/option';
import type {FullCardBenefitFragment} from '@transactions/graphql';
import type {PeriodRow} from 'src/CardBenefitsView/PeriodsEditor';
import type {ScheduleFormState} from 'src/CardBenefitsView/ScheduleEditor';
import type {SystemProps} from 'src/Modals';

export type EditCardBenefitModalUserProps = {
  benefit: FullCardBenefitFragment;
  /** Fee period bounds — the coverage span when re-exploding a schedule. */
  feePeriodStartDate: number;
  feePeriodEndDate: number;
  onSuccess: () => void;
};

type Mode = 'cadence' | 'custom';

// Stored rows are half-open; the row editor shows the inclusive last day
const toPeriodRow = (p: {
  startDate: number;
  endDate: number;
  amount: number;
}): PeriodRow => ({
  startDate: fromSeconds(p.startDate),
  endDate: fromSeconds(p.endDate).minus({days: 1}),
  amount: (p.amount / 100).toFixed(2),
});

export const EditCardBenefitModal = ({
  onRequestClose,
  benefit,
  feePeriodStartDate,
  feePeriodEndDate,
  onSuccess,
}: EditCardBenefitModalUserProps & SystemProps) => {
  const formId = React.useId();
  const {deleteCardBenefitModal} = useModals();
  const [name, setName] = React.useState(benefit.name);
  const [description, setDescription] = React.useState(
    benefit.description ?? '',
  );
  const [category, setCategory] = React.useState<Option<string>>(
    some(benefit.category.name),
  );
  // The DB stores only period rows; recover the schedule (if any) for the
  // high-level editor. Irregular or partial-coverage rows open in the
  // custom period editor.
  const inferred = React.useMemo(
    () =>
      inferSchedule(benefit.periods, {
        startDate: feePeriodStartDate,
        endDate: feePeriodEndDate,
      }),
    [benefit.periods, feePeriodStartDate, feePeriodEndDate],
  );
  const [mode, setMode] = React.useState<Mode>(
    inferred != null ? 'cadence' : 'custom',
  );
  const [schedule, setSchedule] = React.useState<ScheduleFormState>(
    inferred != null ? scheduleStateFromInferred(inferred) : defaultScheduleState,
  );
  const [creditAccountId, setCreditAccountId] = React.useState<string | null>(
    benefit.creditAccountId ?? null,
  );
  const [periods, setPeriods] = React.useState<PeriodRow[]>(
    inferred != null ? [] : benefit.periods.map(toPeriodRow),
  );
  const [error, setError] = React.useState<string | null>(null);

  const [updateBenefit, {loading}] = useUpdateCardBenefitMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const periodInputs =
        mode === 'cadence'
          ? explodeScheduleState(schedule, {
              startDate: feePeriodStartDate,
              endDate: feePeriodEndDate,
            })
          : periods.map((p) => {
              if (!p.startDate.isValid) throw new Error('Invalid start date');
              if (!p.endDate.isValid) throw new Error('Invalid end date');
              const cents = Math.round(parseFloat(p.amount) * 100);
              if (isNaN(cents)) throw new Error(`Invalid amount: ${p.amount}`);
              return {
                startDate: Math.floor(p.startDate.toSeconds()),
                // Editor rows are inclusive; stored bounds are half-open
                endDate: Math.floor(p.endDate.plus({days: 1}).toSeconds()),
                amount: cents,
              };
            });

      await updateBenefit({
        variables: {
          id: benefit._id,
          name: name.trim(),
          description: description.trim() || null,
          category: category.getOrElse(''),
          // Empty string clears the credit account server-side
          creditAccountId: creditAccountId ?? '',
          periods: periodInputs,
        },
        refetchQueries: [
          {
            query: FeePeriodsByAccountDocument,
            variables: {accountId: benefit.accountId},
          },
        ],
        update: (store, result: FetchResult<UpdateCardBenefitMutation>) => {
          const newCategory = result?.data?.updateCardBenefit?.category;
          newCategory && addNewCategory(store, newCategory);
        },
      });
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const handleDelete = () => {
    onRequestClose();
    deleteCardBenefitModal.openModal({benefit, onDeleted: onSuccess});
  };

  // Switching to custom mode starts from the current rows (either the stored
  // ones or a preview of the schedule) so nothing is lost
  const handleSetCustom = () => {
    if (mode === 'custom') return;
    if (periods.length === 0) {
      try {
        setPeriods(
          explodeScheduleState(schedule, {
            startDate: feePeriodStartDate,
            endDate: feePeriodEndDate,
          }).map((p) => toPeriodRow(p)),
        );
      } catch {
        setPeriods(benefit.periods.map(toPeriodRow));
      }
    }
    setMode('custom');
  };

  const isValid =
    name.trim() &&
    !category.isEmpty &&
    (mode === 'cadence' ? schedule.amount.trim() !== '' : periods.length > 0);

  const footer = (
    <>
      <Button variant="destructive" onClick={handleDelete}>
        Delete
      </Button>
      <div className="flex-1" />
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || !isValid}
      >
        {loading ? 'Saving...' : 'Save'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Edit Benefit"
      footer={footer}
      size="lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Name</label>
          <TextInput
            value={name}
            onChange={setName}
            placeholder="Benefit name"
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Description</label>
          <TextInput
            value={description}
            onChange={setDescription}
            placeholder="Optional description"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Category</label>
          <CategoryPicker
            selectedCategory={category}
            onSelectCategory={setCategory}
          />
          <p className="text-muted-foreground text-xs">
            Prefilled when creating transactions for this benefit
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <SegmentedButton
            className="self-start"
            options={[
              {value: 'cadence', label: 'Regular schedule'},
              {value: 'custom', label: 'Custom periods'},
            ]}
            value={mode}
            onChange={(next) =>
              next === 'custom' ? handleSetCustom() : setMode('cadence')
            }
          />

          {mode === 'cadence' ? (
            <ScheduleEditor
              value={schedule}
              onChange={setSchedule}
              bounds={{
                startDate: feePeriodStartDate,
                endDate: feePeriodEndDate,
              }}
            />
          ) : (
            <PeriodsEditor
              periods={periods}
              onChange={setPeriods}
              defaultCustomize={benefit.periods.length !== 1}
              defaultGenStartDate={today()}
              onError={setError}
            />
          )}
        </div>

        <CreditDestinationField
          value={creditAccountId}
          onChange={setCreditAccountId}
          cardAccountId={benefit.accountId}
        />
      </form>
    </Modal>
  );
};
